/**
 * SSSecurityCatalog — Security Objects & Methods Admin (§6.6)
 *
 * Manages sssecurityobjects and sssecurityobjects_methods:
 *   - List / create / edit / delete security objects
 *   - List / create / edit / delete methods on each object
 *   - POST /security/rescan — re-seeds catalog from annotated code
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssSecurityAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Modal, SplitLayout, Section,
  Button, Badge, Field, Input, Select,
  Spinner, EmptyState, ErrorBanner, SuccessBanner, InfoBanner,
  DataTable, InfoGrid, SearchBar,
  COLOR,
} from '../../components/ss';

// ── Object form modal ─────────────────────────────────────────────────────────
function ObjectFormModal({ open, initial, onClose, onDone }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({ securitymodule: '', name: '', description: '', doctypetable: '', doctypefield: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]   = useState('');
  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (initial) setForm({ securitymodule: initial.securitymodule || '', name: initial.name || '', description: initial.description || '', doctypetable: initial.doctypetable || initial.doctype_table || '', doctypefield: initial.doctypefield || initial.doctype_field || '' });
    else setForm({ securitymodule: '', name: '', description: '', doctypetable: '', doctypefield: '' });
    setErr('');
  }, [initial, open]);

  async function submit(e) {
    e.preventDefault();
    if (!form.name) { setErr('Name is required.'); return; }
    if (!form.securitymodule) { setErr('Security Module is required.'); return; }
    setSaving(true); setErr('');
    try {
      if (isEdit) await ssSecurityAPI.updateObject(initial.id ?? initial.securityobjectid, form);
      else        await ssSecurityAPI.createObject(form);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title={isEdit ? 'Edit Security Object' : 'New Security Object'} onClose={onClose}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <Field label="Security Module" required style={{ marginBottom: 14 }}>
          <Input required value={form.securitymodule} onChange={upd('securitymodule')} placeholder="e.g. parties" />
        </Field>
        <Field label="Object Name" required style={{ marginBottom: 14 }}>
          <Input required value={form.name} onChange={upd('name')} placeholder="e.g. Party" />
        </Field>
        <Field label="Description" style={{ marginBottom: 14 }}>
          <Input value={form.description} onChange={upd('description')} />
        </Field>
        <Field label="DocType Table (optional)" style={{ marginBottom: 14 }}>
          <Input value={form.doctypetable} onChange={upd('doctypetable')} placeholder="e.g. ssparties" />
        </Field>
        <Field label="DocType Field (optional)" style={{ marginBottom: 18 }}>
          <Input value={form.doctypefield} onChange={upd('doctypefield')} placeholder="e.g. partyid" />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Object'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Method form modal ─────────────────────────────────────────────────────────
function MethodFormModal({ open, objectId, initial, onClose, onDone }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({ name: '', description: '', defaultaccess: false, usedoctype: false });
  const [saving, setSaving] = useState(false);
  const [err, setErr]   = useState('');
  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const updBool = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));

  useEffect(() => {
    if (initial) setForm({ name: initial.name || initial.methodname || '', description: initial.description || '', defaultaccess: !!initial.defaultaccess, usedoctype: !!initial.usedoctype });
    else setForm({ name: '', description: '', defaultaccess: false, usedoctype: false });
    setErr('');
  }, [initial, open]);

  async function submit(e) {
    e.preventDefault();
    if (!form.name) { setErr('Name is required.'); return; }
    setSaving(true); setErr('');
    try {
      if (isEdit) await ssSecurityAPI.updateMethod(objectId, initial.id ?? initial.securityobjectmethodid, form);
      else        await ssSecurityAPI.createMethod(objectId, form);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title={isEdit ? 'Edit Method' : 'New Method'} onClose={onClose} width={420}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <Field label="Method Name" required style={{ marginBottom: 14 }}>
          <Input required value={form.name} onChange={upd('name')} placeholder="e.g. Create" />
        </Field>
        <Field label="Description" style={{ marginBottom: 14 }}>
          <Input value={form.description} onChange={upd('description')} />
        </Field>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.defaultaccess} onChange={updBool('defaultaccess')} />
            Default Access (allow without explicit grant)
          </label>
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.usedoctype} onChange={updBool('usedoctype')} />
            Use DocType (enables row-level permission scoping)
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save' : 'Create Method'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── Object detail + methods ───────────────────────────────────────────────────
function ObjectDetail({ obj, onClose, onRefresh }) {
  const [methods, setMethods]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [editMethod, setEditMethod] = useState(null);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [err, setErr]               = useState('');
  const [saving, setSaving]         = useState(false);

  const objId = obj ? (obj.id ?? obj.securityobjectid) : null;

  const loadMethods = useCallback(() => {
    if (!obj) return;
    setLoading(true);
    ssSecurityAPI.listMethods(obj.id ?? obj.securityobjectid)
      .then(d => setMethods(d.data || d || []))
      .catch(ex => setErr(ex.response?.data?.detail || ex.message))
      .finally(() => setLoading(false));
  }, [obj]);

  useEffect(() => { if (obj) loadMethods(); else setMethods([]); setErr(''); }, [obj, loadMethods]);

  if (!obj) return <EmptyState icon="🔐" title="Select a security object" message="Click an object on the left to manage its methods." />;

  const deleteMethod = async (mid) => {
    if (!window.confirm('Delete this method?')) return;
    setSaving(true);
    try { await ssSecurityAPI.deleteMethod(objId, mid); loadMethods(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setSaving(false); }
  };

  const METHOD_COLS = [
    { key: 'name', label: 'Method', render: v => <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{v}</span> },
    { key: 'defaultaccess', label: 'Default', render: v => v ? <Badge color="green">Allow</Badge> : <Badge color="gray">Deny</Badge> },
    { key: 'usedoctype',    label: 'DocType',  render: v => v ? <Badge color="blue">Yes</Badge> : <Badge color="gray">No</Badge> },
    { key: '_edit', label: '', render: (_, m) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button variant="ghost" size="sm" onClick={() => setEditMethod(m)}>Edit</Button>
        <Button variant="danger" size="sm" onClick={() => deleteMethod(m.id ?? m.securityobjectmethodid)}>✕</Button>
      </div>
    )},
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: COLOR.text }}>{obj.name}</div>
          <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>ID #{objId}{obj.description ? ` · ${obj.description}` : ''}</div>
          {(obj.doctypetable || obj.doctype_table) && <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>DocType: {obj.doctypetable || obj.doctype_table}.{obj.doctypefield || obj.doctype_field}</div>}
        </div>
        <Button variant="ghost" onClick={onClose}>×</Button>
      </div>

      <ErrorBanner>{err}</ErrorBanner>

      <Section
        title={`Methods (${methods.length})`}
        action={<Button size="sm" onClick={() => setShowAddMethod(true)}>+ Add Method</Button>}
      >
        {loading ? <Spinner /> : (
          <DataTable columns={METHOD_COLS} rows={methods} rowKey={m => m.id ?? m.securityobjectmethodid} emptyIcon="⚙️" emptyTitle="No methods defined" />
        )}
      </Section>

      <MethodFormModal open={showAddMethod} objectId={objId} initial={null} onClose={() => setShowAddMethod(false)} onDone={() => { setShowAddMethod(false); loadMethods(); }} />
      <MethodFormModal open={!!editMethod} objectId={objId} initial={editMethod} onClose={() => setEditMethod(null)} onDone={() => { setEditMethod(null); loadMethods(); }} />
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SSSecurityCatalog() {
  const [objects, setObjects]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [err, setErr]             = useState('');
  const [ok, setOk]               = useState('');
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editObj, setEditObj]     = useState(null);
  const [rescanning, setRescanning] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    ssSecurityAPI.listObjects()
      .then(d => { setObjects(d.data || d || []); setErr(''); })
      .catch(ex => setErr(ex.response?.data?.detail || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const rescan = async () => {
    setRescanning(true); setErr(''); setOk('');
    try { await ssSecurityAPI.rescan(); setOk('Security catalog reseeded from annotated code.'); load(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
    finally { setRescanning(false); }
  };

  const deleteObj = async (obj) => {
    if (!window.confirm(`Delete security object "${obj.name}"?`)) return;
    const oid = obj.id ?? obj.securityobjectid;
    try { await ssSecurityAPI.deleteObject(oid); load(); if ((selected?.id ?? selected?.securityobjectid) === oid) setSelected(null); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
  };

  const filtered = objects.filter(o => !search || o.name?.toLowerCase().includes(search.toLowerCase()) || o.description?.toLowerCase().includes(search.toLowerCase()));

  const OBJ_COLS = [
    { key: 'name',        label: 'Object',      render: v => <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{v}</span> },
    { key: 'description', label: 'Description', render: v => v || <span style={{ color: COLOR.grayLight }}>—</span> },
    { key: 'doctypetable', label: 'DocType',   render: (v, o) => v || o.doctype_table || <span style={{ color: COLOR.grayLight }}>—</span> },
    { key: '_actions', label: '', render: (_, o) => (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setEditObj(o); }}>Edit</Button>
        <Button variant="danger" size="sm" onClick={e => { e.stopPropagation(); deleteObj(o); }}>✕</Button>
      </div>
    )},
  ];

  return (
    <Page>
      <PageHeader
        title="Security Catalog"
        subtitle="Manage securable objects and methods (sssecurityobjects + sssecurityobjects_methods)"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={rescan} disabled={rescanning}>{rescanning ? 'Rescanning…' : '🔄 Rescan Code'}</Button>
            <Button onClick={() => setShowCreate(true)}>+ New Object</Button>
          </div>
        }
      />

      <ErrorBanner>{err}</ErrorBanner>
      <SuccessBanner>{ok}</SuccessBanner>
      <InfoBanner>The security catalog is auto-seeded at boot by scanning <code>@require()</code> decorators on API routes. Use Rescan to refresh after deploying new endpoints.</InfoBanner>

      <SplitLayout
        listWidth={400}
        list={
          <Card>
            <SearchBar value={search} onChange={setSearch} placeholder="Search objects…" style={{ marginBottom: 14 }} />
            <DataTable
              columns={OBJ_COLS}
              rows={filtered}
              rowKey={o => o.id ?? o.securityobjectid}
              loading={loading}
              onRowClick={setSelected}
              selectedKey={selected ? (selected.id ?? selected.securityobjectid) : undefined}
              emptyIcon="🔐"
              emptyTitle="No security objects"
              emptyMsg="Use Rescan to seed from code, or create manually."
            />
          </Card>
        }
        detail={
          <Card style={{ minHeight: 400 }}>
            <ObjectDetail obj={selected} onClose={() => setSelected(null)} onRefresh={load} />
          </Card>
        }
      />

      <ObjectFormModal open={showCreate} initial={null} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />
      <ObjectFormModal open={!!editObj} initial={editObj} onClose={() => setEditObj(null)} onDone={() => { setEditObj(null); load(); }} />
    </Page>
  );
}
